import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import type { PatientModuleDataResponse } from '@features/patient-context-bar';
import { useLazyGetPatientModuleDataQuery } from '@features/patient-context-bar';
import type { PatientCoverage } from '@features/patient-coverages';
import { useLazyGetPatientCoveragesQuery } from '@features/patient-coverages';
import { useEventTracking } from '@services/statsig';
import { useSnackbar } from 'notistack';

import type { DemographicSelections } from '../components/merge-customization/DemographicFieldSelector';
import { DEMOGRAPHIC_FIELDS } from '../components/merge-customization/DemographicFieldSelector';
import { useMergePatientGroupMutation } from '../store';
import type {
  CoverageSelections,
  MergeCustomizationLocationState,
  ModuleItemSelection,
  NormalizedModuleItem,
  NormalizedModuleMap,
  PatientCoverageSummary,
  PatientSummaryWithCoverages,
  SelectionKey,
} from '../types/merge-customization';
import { makeSelectionKey } from '../types/merge-customization';
import { isIdField, isTechnicalValue } from '../utils/fieldFilters';

// ---------------------------------------------------------------------------
// Coverage mapping helper
// ---------------------------------------------------------------------------

/** Map the richer CoverageResponse from the dedicated endpoint to PatientCoverageSummary. */
function mapCoverageResponse(cov: PatientCoverage): PatientCoverageSummary {
  return {
    coverage_order: cov.coverage_order,
    member_id: cov.member_id,
    patient_insurance_coverage_id: cov.patient_insurance_coverage_id,
    payer_name: cov.payer_name,
    plan_name: cov.plan_name,
    status: cov.status,
    updated_at: cov.updated_at,
    group_number: cov.group_number,
    kind: cov.kind,
    effective_date: cov.effective_date,
    termination_date: cov.termination_date,
  };
}

// ---------------------------------------------------------------------------
// Module normalization helpers
// ---------------------------------------------------------------------------

const MODULE_SUFFIXES = ['_ptmodule', '_testmodule'];

/**
 * Derive a canonical module name from the module key or API name field.
 * e.g. "diagnoses_ptmodule" → "diagnoses"
 *      "autism_spectrum_disorder_screening_testmodule" → "autism_spectrum_disorder_screening"
 */
function deriveCanonicalName(moduleKey: string, apiName?: string): string {
  if (apiName) return apiName.toLowerCase();
  let name = moduleKey.toLowerCase();
  for (const suffix of MODULE_SUFFIXES) {
    if (name.endsWith(suffix)) {
      name = name.slice(0, -suffix.length);
      break;
    }
  }
  return name;
}

/** Title-case a snake_case module name for display. */
function formatDisplayName(canonicalName: string): string {
  return canonicalName
    .split('_')
    .map(word => word.charAt(0).toUpperCase() + word.slice(1))
    .join(' ');
}

// ---------------------------------------------------------------------------
// Schema-driven display label
// ---------------------------------------------------------------------------

interface SchemaField {
  name: string;
  description: string;
  required: boolean;
  type: string; // 'bool' | 'date' | 'number' | string (text)
}

/** Extract the first date value from an item, guided by schema when available. */
function extractTimestamp(
  data: Record<string, unknown>,
  schema: SchemaField[] | null,
): string | null {
  if (schema) {
    for (const field of schema) {
      if (field.type === 'date') {
        const val = data[field.name];
        if (typeof val === 'string' && val) return val;
      }
    }
  }
  // Fallback: look for common date field names
  for (const key of Object.keys(data)) {
    if (key === 'date' || key.endsWith('_date') || key.endsWith('_at')) {
      const val = data[key];
      if (typeof val === 'string' && val && !isNaN(Date.parse(val))) return val;
    }
  }
  return null;
}

/**
 * Build a human-readable display label for a module item.
 *
 * Strategy (mirrors TableBlock's schema-driven approach):
 * 1. If a schema is provided, use it to pick the best display-worthy fields
 *    — skip bools, dates, and ID fields; prefer string fields first.
 * 2. If no schema, inspect the data keys directly with the same heuristics.
 */
function getDisplayLabel(
  data: Record<string, unknown>,
  schema: SchemaField[] | null,
): string {
  if (schema && schema.length > 0) {
    return getDisplayLabelFromSchema(data, schema);
  }
  return getDisplayLabelFromData(data);
}

/** Pick up to 2 display-worthy field values guided by the schema. */
function getDisplayLabelFromSchema(
  data: Record<string, unknown>,
  schema: SchemaField[],
): string {
  // Partition schema fields into preferred (text) vs secondary (number)
  const textFields: SchemaField[] = [];
  const numberFields: SchemaField[] = [];

  for (const field of schema) {
    if (isIdField(field.name)) continue;
    if (field.type === 'bool' || field.type === 'date') continue;
    if (field.type === 'number') {
      numberFields.push(field);
    } else {
      textFields.push(field);
    }
  }

  // Prioritize code-like fields first so codes appear on the left
  textFields.sort((a, b) => {
    const aIsCode = a.name.toLowerCase().includes('code');
    const bIsCode = b.name.toLowerCase().includes('code');
    if (aIsCode && !bIsCode) return -1;
    if (!aIsCode && bIsCode) return 1;
    return 0;
  });

  const parts: string[] = [];

  // Try text fields first
  for (const field of textFields) {
    const val = data[field.name];
    if (typeof val === 'string' && val && !isTechnicalValue(val)) {
      parts.push(val);
    }
    if (parts.length >= 2) break;
  }

  // Fill with number fields if we still need more context
  if (parts.length < 2) {
    for (const field of numberFields) {
      const val = data[field.name];
      if (typeof val === 'number') {
        const label = formatDisplayName(field.name);
        parts.push(`${label}: ${val}`);
      }
      if (parts.length >= 2) break;
    }
  }

  return parts.join(' — ') || 'Item';
}

/** Fallback: inspect data keys directly when no schema is available. */
function getDisplayLabelFromData(data: Record<string, unknown>): string {
  const parts: string[] = [];

  for (const [key, val] of Object.entries(data)) {
    if (isIdField(key)) continue;
    if (typeof val === 'string' && val) {
      if (isTechnicalValue(val)) continue;
      parts.push(val);
    } else if (typeof val === 'number') {
      const label = formatDisplayName(key);
      parts.push(`${label}: ${val}`);
    }
    if (parts.length >= 2) break;
  }

  return parts.join(' — ') || 'Item';
}

/** Normalize a single patient's module data response into items. */
function normalizePatientModules(
  rawData: PatientModuleDataResponse | undefined,
  patientId: string,
  patientName: string,
): NormalizedModuleItem[] {
  const patientModulesData = rawData?.patient_data?.patient_module_data;
  if (!patientModulesData) return [];

  const items: NormalizedModuleItem[] = [];

  for (const [moduleKey, moduleData] of Object.entries(patientModulesData)) {
    const mod = moduleData as {
      name?: string;
      type?: string;
      current_data?: Record<string, unknown[]>;
      schema?: SchemaField[] | null;
    };
    const moduleName = deriveCanonicalName(moduleKey, mod.name);
    const schema = mod.schema ?? null;

    if (!mod.current_data) continue;

    for (const [dataKey, dataArray] of Object.entries(mod.current_data)) {
      if (!Array.isArray(dataArray)) continue;

      dataArray.forEach((rawItem, itemIndex) => {
        const data = rawItem as Record<string, unknown>;
        items.push({
          data,
          patientId,
          patientName,
          moduleKey,
          moduleName,
          dataKey,
          itemIndex,
          selectionKey: makeSelectionKey(patientId, moduleKey, itemIndex),
          displayLabel: getDisplayLabel(data, schema),
          timestamp: extractTimestamp(data, schema),
        });
      });
    }
  }

  return items;
}

/** Group flat items into a NormalizedModuleMap keyed by canonical module name. */
function buildModuleMap(allItems: NormalizedModuleItem[]): NormalizedModuleMap {
  const map: NormalizedModuleMap = {};

  for (const item of allItems) {
    const canonicalName = item.moduleName;

    if (!map[canonicalName]) {
      map[canonicalName] = {
        name: formatDisplayName(canonicalName),
        items: [],
      };
    }
    map[canonicalName].items.push(item);
  }

  // Sort items alphabetically by display label within each module
  for (const mod of Object.values(map)) {
    mod.items.sort((a, b) => a.displayLabel.localeCompare(b.displayLabel));
  }

  return map;
}

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

export interface UseMergeCustomizationReturn {
  /** Normalized module data grouped by module name */
  modules: NormalizedModuleMap;
  /** Set of selected item keys */
  selections: Set<SelectionKey>;
  /** Toggle an individual item */
  toggleItem: (key: SelectionKey) => void;
  /** Select all items from a specific patient within a module */
  selectAllFromPatient: (moduleName: string, patientId: string) => void;
  /** Total count of selected items */
  selectedCount: number;
  /** Whether module data is still loading */
  isLoading: boolean;
  /** Whether coverage data is still loading */
  isCoverageLoading: boolean;
  /** Whether the merge is in progress */
  isMerging: boolean;
  /** Errors from fetching module data (patientId → error message) */
  fetchErrors: Record<string, string>;
  /** Confirm and execute the merge */
  confirmMerge: () => Promise<void>;
  /** Patient color map for consistent chip colors */
  patientColors: Map<string, string>;
  /** Auto-resolved default patient ID (patients[0]) — used for demographics default */
  defaultPatientId: string;
  /** Patients still active in the merge pool (may include coverages) */
  activePatients: PatientSummaryWithCoverages[];

  // --- Demographic field selection ---
  /** Per-field source patient selections for demographics */
  demographicSelections: DemographicSelections;
  /** Change which patient's value to use for a demographic field */
  setDemographicFieldSource: (patchKey: string, patientId: string) => void;

  // --- Coverage slot selection ---
  /** Per-slot coverage selections (slot → coverageId). Managed by CoverageSlotSelector. */
  coverageSelections: CoverageSelections;
  /** Batch-set all coverage selections (called by CoverageSlotSelector on any change) */
  setCoverageSelections: (selections: CoverageSelections) => void;
}

const PATIENT_COLORS = [
  '#1976d2', // blue
  '#d32f2f', // red
  '#2e7d32', // green
  '#9c27b0', // purple
  '#ed6c02', // orange
];

/** Shuffle an array in-place using a seeded PRNG (Mulberry32). */
function seededShuffle<T>(arr: T[], seed: number): T[] {
  let s = seed | 0;
  for (let i = arr.length - 1; i > 0; i--) {
    s = (s + 0x6d2b79f5) | 0;
    let t = Math.imul(s ^ (s >>> 15), 1 | s);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    const r = ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    const j = Math.floor(r * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }
  return arr;
}

/** Simple string → seed integer. */
function hashString(str: string): number {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  return hash;
}

export function useMergeCustomization(
  locationState: MergeCustomizationLocationState,
): UseMergeCustomizationReturn {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { patientDeduplication } = useEventTracking();

  const { group, organizationId } = locationState;

  // Auto-resolved default patient (first in the group) — used for demographics and API payload
  const defaultPatientId = group.patients[0]?.patient_id ?? '';

  // --- Data fetching ---
  const [triggerFetch] = useLazyGetPatientModuleDataQuery();
  const [triggerCoverageFetch] = useLazyGetPatientCoveragesQuery();
  const [mergePatientGroup, { isLoading: isMerging }] = useMergePatientGroupMutation();

  // --- Demographic field selections (patchKey → patientId) ---
  const [demographicSelections, setDemographicSelections] = useState<DemographicSelections>({});

  const setDemographicFieldSource = useCallback((patchKey: string, patientId: string) => {
    setDemographicSelections(prev => ({ ...prev, [patchKey]: patientId }));
  }, []);

  // --- Coverage slot selections (slot → coverageId, managed by CoverageSlotSelector) ---
  const [coverageSelections, setCoverageSelections] = useState<CoverageSelections>({});

  const [rawResponses, setRawResponses] = useState<Record<string, PatientModuleDataResponse>>({});
  const [coverageResponses, setCoverageResponses] = useState<Record<string, PatientCoverageSummary[]>>({});
  const [fetchErrors, setFetchErrors] = useState<Record<string, string>>({});
  const [isLoading, setIsLoading] = useState(true);
  const [isCoverageLoading, setIsCoverageLoading] = useState(true);
  const fetchedRef = useRef(false);

  // Fetch module data + coverage data for all patients in parallel
  useEffect(() => {
    if (fetchedRef.current) return;
    fetchedRef.current = true;

    const fetchAll = async () => {
      setIsLoading(true);
      setIsCoverageLoading(true);
      const moduleResults: Record<string, PatientModuleDataResponse> = {};
      const coverageResults: Record<string, PatientCoverageSummary[]> = {};
      const errors: Record<string, string> = {};

      await Promise.all(
        group.patients.map(async patient => {
          const pid = patient.patient_id;

          // Fetch modules + coverages per-patient in parallel
          const [moduleResult, coverageResult] = await Promise.allSettled([
            triggerFetch({ patientId: pid }).unwrap(),
            triggerCoverageFetch({ patientId: pid }).unwrap(),
          ]);

          if (moduleResult.status === 'fulfilled') {
            moduleResults[pid] = moduleResult.value;
          } else {
            errors[pid] =
              moduleResult.reason instanceof Error
                ? moduleResult.reason.message
                : 'Failed to load module data';
          }

          if (coverageResult.status === 'fulfilled') {
            coverageResults[pid] = coverageResult.value.coverages.map(mapCoverageResponse);
          }
          // Coverage fetch failures are non-critical; fall back to inline data
        }),
      );

      setRawResponses(moduleResults);
      setCoverageResponses(coverageResults);
      setFetchErrors(errors);
      setIsLoading(false);
      setIsCoverageLoading(false);
    };

    void fetchAll();
  }, [group.patients, triggerFetch, triggerCoverageFetch]);

  // All patients are active (no removal UI in this redesign).
  // Inject fetched coverages (from dedicated endpoint) onto each patient
  // so downstream components get the richer coverage data transparently.
  const activePatients = useMemo<PatientSummaryWithCoverages[]>(
    () =>
      group.patients.map(p => ({
        ...p,
        coverages: coverageResponses[p.patient_id] ?? [],
      })),
    [group.patients, coverageResponses],
  );

  // --- Patient color map ---
  const patientColors = useMemo(() => {
    // Shuffle palette with a seed from the group ID so the order
    // looks random but stays stable for the same group.
    const shuffled = seededShuffle([...PATIENT_COLORS], hashString(group.group_id));
    const colorMap = new Map<string, string>();
    group.patients.forEach((p, i) => {
      colorMap.set(p.patient_id, shuffled[i % shuffled.length]);
    });
    return colorMap;
  }, [group.patients, group.group_id]);

  // --- Normalize module data ---
  const modules = useMemo<NormalizedModuleMap>(() => {
    if (isLoading) return {};

    const allItems: NormalizedModuleItem[] = [];

    for (const patient of group.patients) {
      const raw = rawResponses[patient.patient_id];
      const name = [patient.first_name, patient.last_name].filter(Boolean).join(' ') || patient.patient_id.substring(0, 8);
      const items = normalizePatientModules(
        raw,
        patient.patient_id,
        name,
      );
      allItems.push(...items);
    }

    return buildModuleMap(allItems);
  }, [isLoading, rawResponses, group.patients]);

  // --- Selection state ---
  const [selections, setSelections] = useState<Set<SelectionKey>>(new Set());
  const initializedRef = useRef(false);

  // Initialize selections: pre-check ALL items (additive merge — users deselect what they don't want)
  useEffect(() => {
    if (isLoading || initializedRef.current) return;
    initializedRef.current = true;

    const initial = new Set<SelectionKey>();
    for (const mod of Object.values(modules)) {
      for (const item of mod.items) {
        initial.add(item.selectionKey);
      }
    }
    setSelections(initial);
  }, [isLoading, modules]);

  const toggleItem = useCallback((key: SelectionKey) => {
    setSelections(prev => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const selectAllFromPatient = useCallback(
    (moduleName: string, patientId: string) => {
      const mod = modules[moduleName];
      if (!mod) return;

      setSelections(prev => {
        const next = new Set(prev);
        // Remove all items from this module, then add only the target patient's
        for (const item of mod.items) {
          next.delete(item.selectionKey);
        }
        for (const item of mod.items) {
          if (item.patientId === patientId) {
            next.add(item.selectionKey);
          }
        }
        return next;
      });
    },
    [modules],
  );

  const selectedCount = selections.size;

  // --- Build merge payload ---
  const buildModuleSelections = useCallback((): ModuleItemSelection[] => {
    // Build a lookup from selectionKey → item for O(1) access
    const itemByKey = new Map(
      Object.values(modules).flatMap(mod => mod.items.map(item => [item.selectionKey, item] as const)),
    );

    const result: ModuleItemSelection[] = [];
    for (const key of selections) {
      const item = itemByKey.get(key);
      if (item) {
        result.push({
          module_name: item.moduleKey,
          source_patient_id: item.patientId,
          item_index: item.itemIndex,
        });
      }
    }
    return result;
  }, [selections, modules]);

  // --- Build demographic overrides payload ---
  const buildDemographicOverrides = useCallback((): Record<string, string | null> | null => {
    const patientById = new Map(activePatients.map(p => [p.patient_id, p]));
    const overrides: Record<string, string | null> = {};

    for (const field of DEMOGRAPHIC_FIELDS) {
      const sourcePatientId = demographicSelections[field.patchKey];
      if (!sourcePatientId || sourcePatientId === defaultPatientId) continue;

      const sourcePatient = patientById.get(sourcePatientId);
      if (!sourcePatient) continue;

      const value = sourcePatient[field.summaryKey];
      overrides[field.patchKey] = value != null ? String(value) : null;
    }

    return Object.keys(overrides).length > 0 ? overrides : null;
  }, [activePatients, demographicSelections, defaultPatientId]);

  // --- Build coverage selections payload ---
  const buildCoverageSelectionsPayload = useCallback((): Record<string, string> | null => {
    return Object.keys(coverageSelections).length > 0 ? coverageSelections : null;
  }, [coverageSelections]);

  // --- Confirm merge ---
  const confirmMerge = useCallback(async () => {
    const secondaryPatientIds = activePatients
      .filter(p => p.patient_id !== defaultPatientId)
      .map(p => p.patient_id);

    try {
      const moduleSelections = buildModuleSelections();
      const demographicOverrides = buildDemographicOverrides();
      const coverageSelectionsPayload = buildCoverageSelectionsPayload();
      await mergePatientGroup({
        mergeGroupRequest: {
          organization_id: organizationId,
          patient_match_group: group.group_id,
          primary_patient_id: defaultPatientId,
          patient_ids_to_merge: secondaryPatientIds,
          module_selections: moduleSelections,
          demographic_overrides: demographicOverrides,
          coverage_selections: coverageSelectionsPayload,
        },
      }).unwrap();

      patientDeduplication.mergeCompleted({
        group_id: group.group_id,
        primary_patient_id: defaultPatientId,
        merged_patient_count: secondaryPatientIds.length + 1,
      });

      enqueueSnackbar('Patients merged successfully', { variant: 'success' });
      navigate('/patients/merge');
    } catch (error) {
      patientDeduplication.mergeFailed(
        error instanceof Error ? error.message : 'Unknown error',
        {
          group_id: group.group_id,
          primary_patient_id: defaultPatientId,
        },
      );

      enqueueSnackbar('Failed to merge patients', { variant: 'error' });
    }
  }, [
    activePatients,
    defaultPatientId,
    organizationId,
    group.group_id,
    buildModuleSelections,
    buildDemographicOverrides,
    buildCoverageSelectionsPayload,
    mergePatientGroup,
    patientDeduplication,
    enqueueSnackbar,
    navigate,
  ]);

  return {
    modules,
    selections,
    toggleItem,
    selectAllFromPatient,
    selectedCount,
    isLoading,
    isCoverageLoading,
    isMerging,
    fetchErrors,
    confirmMerge,
    patientColors,
    defaultPatientId,
    activePatients,
    demographicSelections,
    setDemographicFieldSource,
    coverageSelections,
    setCoverageSelections,
  };
}
